import { useEffect, useState, useRef } from "react";
import api, { formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Trash2, Save, Edit3, Users, X, Lock, Crown, Shield, ShieldCheck, Star, User, Award, Flame, Upload } from "lucide-react";

const PERMISSIONS = [
  { key: "forum.read", label: "Citește forum" },
  { key: "forum.post", label: "Postează în forum" },
  { key: "forum.delete_own", label: "Șterge propriile postări" },
  { key: "forum.delete_any", label: "Șterge orice postare" },
  { key: "moderation.approve", label: "Aprobă postări (mod queue)" },
  { key: "moderation.warn", label: "Trimite avertismente" },
  { key: "admin.access", label: "Acces Admin Panel" },
  { key: "admin.users", label: "Editează utilizatori" },
  { key: "admin.settings", label: "Modifică setări site" },
  { key: "vip.features", label: "Acces feature VIP" },
];

const ICONS = ["users", "crown", "shield", "shield-check", "star", "user", "award", "flame"];

const iconMap = {
  users: Users,
  crown: Crown,
  shield: Shield,
  "shield-check": ShieldCheck,
  star: Star,
  user: User,
  award: Award,
  flame: Flame,
};

const GroupIcon = ({ icon, color, className }) => {
  if (!icon) return <Users className={className} style={{ color }} />;
  if (icon.startsWith("http") || icon.includes("/")) {
    return <img src={icon} alt="Icon" className={`object-contain ${className}`} style={{ width: 16, height: 16 }} />;
  }
  const IconCmp = iconMap[icon] || Users;
  return <IconCmp className={className} style={{ color }} />;
};

const emptyGroup = () => ({
  name: "",
  slug: "",
  description: "",
  color: "#71717a",
  label_icon: "users",
  permissions: {},
  show_in_legend: true,
  legend_order: 99,
  parent_id: "",
  category: "Altele",
});

export default function IDGGroupsTab() {
  const { user: currentUser } = useAuth();
  const isRoot = currentUser?.role === "root";
  const [groups, setGroups] = useState([]);
  const [editing, setEditing] = useState(null);
  const fileInputRef = useRef(null);
  const queryClient = useQueryClient();

  const load = () => api.get("/idg/groups").then((r) => setGroups(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      if (editing.id) {
        await api.patch(`/idg/groups/${editing.id}`, editing);
        toast.success("Grup actualizat");
      } else {
        await api.post("/idg/groups", editing);
        toast.success("Grup creat");
      }
      queryClient.invalidateQueries({ queryKey: ["legendGroups"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["boards"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      setEditing(null); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!window.confirm("Sigur ștergi acest grup?")) return;
    try { 
      await api.delete(`/idg/groups/${id}`); 
      toast.success("Șters"); 
      queryClient.invalidateQueries({ queryKey: ["legendGroups"] });
      queryClient.invalidateQueries({ queryKey: ["groups"] });
      queryClient.invalidateQueries({ queryKey: ["boards"] });
      queryClient.invalidateQueries({ queryKey: ["me"] });
      load(); 
    }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const uploadIcon = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const { data } = await api.post("/idg/groups/upload-icon", formData);
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL || ""}${data.url}`;
      setEditing({ ...editing, label_icon: fullUrl });
      toast.success("Iconiță încărcată");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="idg-groups-tab">
      {!editing ? (
        <>
          <div className="flex items-center justify-between">
            <h3 className="tf-section-title">Grupuri ({groups.length})</h3>
            <button onClick={() => setEditing(emptyGroup())} className="tf-btn-primary tf-clip-btn text-xs flex items-center gap-1" data-testid="idg-group-new">
              <Plus className="w-3.5 h-3.5" /> Grup nou
            </button>
          </div>
          <div className="grid gap-2">
            {groups.filter(g => !g.parent_id).sort((a,b) => a.legend_order - b.legend_order).map((g) => {
              const children = groups.filter(c => c.parent_id === g.id).sort((a,b) => a.legend_order - b.legend_order);
              return (
                <div key={g.id} className="space-y-2">
                  <div className="tf-surface tf-clip-card px-4 py-3 flex items-center gap-3" data-testid={`idg-group-row-${g.id}`}>
                    <div className="w-1 h-10 rounded" style={{ background: g.color }} />
                    <GroupIcon icon={g.label_icon} color={g.color} className="w-4 h-4 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="tf-heading font-semibold text-white flex items-center gap-2">
                        {g.name}
                        {g.is_system && <Lock className="w-3 h-3 text-zinc-500" title="System group" />}
                      </div>
                      <div className="text-[10px] tf-mono text-zinc-500">{g.description || `/${g.slug}`}</div>
                    </div>
                    <span className="text-[10px] tf-mono text-zinc-400">{g.member_count} membri</span>
                    <button onClick={() => setEditing(g)} className="text-zinc-400 hover:text-white p-1" data-testid={`idg-group-edit-${g.id}`}><Edit3 className="w-4 h-4" /></button>
                    {!g.is_system && (
                      <button onClick={() => remove(g.id)} className="text-red-500 hover:bg-red-500/10 p-1" data-testid={`idg-group-delete-${g.id}`}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </div>
                  {children.map(c => (
                    <div key={c.id} className="tf-surface tf-clip-card px-4 py-2 flex items-center gap-3 ml-8 border-l-2 border-white/5 opacity-80 hover:opacity-100 transition-opacity" data-testid={`idg-group-row-${c.id}`}>
                      <div className="w-1 h-8 rounded" style={{ background: c.color }} />
                      <GroupIcon icon={c.label_icon} color={c.color} className="w-3.5 h-3.5 shrink-0" />
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <div className="text-xs font-semibold text-white">{c.name}</div>
                        <div className="text-[10px] tf-mono text-zinc-500 hidden sm:block">{c.description || `/${c.slug}`}</div>
                      </div>
                      <span className="text-[10px] tf-mono text-zinc-400">{c.member_count} membri</span>
                      <button onClick={() => setEditing(c)} className="text-zinc-400 hover:text-white p-1" data-testid={`idg-group-edit-${c.id}`}><Edit3 className="w-4 h-4" /></button>
                      <button onClick={() => remove(c.id)} className="text-red-500 hover:bg-red-500/10 p-1" data-testid={`idg-group-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </>
      ) : (
        <div className="tf-surface tf-clip-card p-5 space-y-3" data-testid="idg-group-form">
          <div className="flex items-center justify-between">
            <h3 className="tf-section-title">{editing.id ? "Editează grup" : "Grup nou"}</h3>
            <button onClick={() => setEditing(null)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          {editing.is_system && !isRoot && (
            <div className="bg-blue-500/10 border border-blue-500/40 text-blue-300 text-xs px-3 py-2 tf-clip-card-sm">
              Grup de sistem — poți edita doar culoare, iconiță, descriere, ordinea în legendă și permisiunile.
            </div>
          )}
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Nume" value={editing.name} onChange={(v) => setEditing({ ...editing, name: v })} disabled={editing.is_system && !isRoot} testid="group-name" />
            <Field label="Slug" value={editing.slug} onChange={(v) => setEditing({ ...editing, slug: v })} disabled={editing.is_system && !isRoot} testid="group-slug" />
            <Field label="Descriere" value={editing.description} onChange={(v) => setEditing({ ...editing, description: v })} testid="group-description" />
            <div>
              <label className="text-xs tf-section-title block mb-1">Grup Părinte (Opțional)</label>
              <select 
                className="tf-input disabled:opacity-50" 
                value={editing.parent_id || ""} 
                onChange={(e) => setEditing({ ...editing, parent_id: e.target.value || null })} 
              disabled={editing.is_system && !isRoot}
                data-testid="group-parent"
              >
                <option value="">Niciunul (Grup Principal)</option>
                {groups.filter(g => g.id !== editing.id && !g.parent_id).map(g => (
                  <option key={g.id} value={g.id}>{g.name}</option>
                ))}
              </select>
            </div>
            {!editing.parent_id && (
              <Field label="Categorie Legendă" value={editing.category} onChange={(v) => setEditing({ ...editing, category: v })} testid="group-category" />
            )}
            <div>
              <label className="text-xs tf-section-title block mb-1">Culoare</label>
              <div className="flex items-center gap-2">
                <input type="color" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="w-12 h-10 tf-clip-card-sm bg-transparent" data-testid="group-color" />
                <input type="text" className="tf-input flex-1" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="text-xs tf-section-title block mb-1">Iconiță</label>
              <div className="flex items-center gap-2">
                 <select className="tf-input flex-1" value={editing.label_icon?.startsWith("http") || editing.label_icon?.includes("/") ? "custom" : editing.label_icon} onChange={(e) => {
                  if (e.target.value !== "custom") setEditing({ ...editing, label_icon: e.target.value });
                }} data-testid="group-icon">
                  {ICONS.map((i) => <option key={i} value={i}>{i}</option>)}
                  <option value="custom">Fișier (Custom)</option>
                </select>
                <button onClick={() => fileInputRef.current?.click()} className="tf-btn-secondary tf-clip-btn text-xs px-2 py-1" title="Încarcă iconiță">
                  <Upload className="w-4 h-4" />
                </button>
                <input type="file" className="hidden" ref={fileInputRef} onChange={uploadIcon} accept="image/*" />
                {(editing.label_icon?.startsWith("http") || editing.label_icon?.includes("/")) && (
                  <img src={editing.label_icon} className="w-6 h-6 object-contain" alt="Current icon" />
                )}
              </div>
            </div>
            <Field label="Ordine în legendă" type="number" value={editing.legend_order} onChange={(v) => setEditing({ ...editing, legend_order: parseInt(v) || 0 })} testid="group-legend-order" />
          </div>
          <label className="flex items-center gap-2 text-xs text-zinc-300">
            <input type="checkbox" checked={!!editing.show_in_legend} onChange={(e) => setEditing({ ...editing, show_in_legend: e.target.checked })} data-testid="group-show-in-legend" />
            Afișează în legendă forum
          </label>
          <div className="border-t border-white/5 pt-3">
            <h4 className="tf-section-title text-[10px] mb-2">Permisiuni</h4>
            <div className="grid md:grid-cols-2 gap-2">
              {PERMISSIONS.map((p) => (
                <label key={p.key} className="flex items-center gap-2 text-xs text-zinc-300 px-2 py-1.5 tf-surface-elev tf-clip-card-sm">
                  <input type="checkbox" checked={!!editing.permissions?.[p.key]} onChange={(e) => setEditing({ ...editing, permissions: { ...editing.permissions, [p.key]: e.target.checked } })} data-testid={`group-perm-${p.key}`} />
                  {p.label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <button onClick={save} className="tf-btn-primary tf-clip-btn flex items-center gap-1" data-testid="group-save"><Save className="w-3.5 h-3.5" /> Salvează</button>
            <button onClick={() => setEditing(null)} className="tf-btn-secondary tf-clip-btn">Anulează</button>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", disabled, testid }) {
  return (
    <div>
      <label className="text-xs tf-section-title block mb-1">{label}</label>
      <input type={type} disabled={disabled} className="tf-input disabled:opacity-50" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </div>
  );
}
